// Visual at-a-glance summary of where the orders in an intake batch currently
// sit. Admin asked for this during UAT round 2: "show how many are in what all
// status… so the admin can follow up that these many orders from this past
// date are still not been dispatched".
//
// 13 lifecycle stages are too fine to scan. We collapse into 5 action-oriented
// buckets matching how Kalpana triages:
//   Needs attention → blocked / data_incomplete / reship_unlinked / needs_attention
//   With Dispatch   → intake / in_review owned by dispatch team
//   With Ops        → any stage currently owned by ops (on_hold, in_review-ops, etc.)
//   With vendor     → ready_for_dispatch / with_vendor / in_transit
//   Closed          → completed / cancelled / delivered_unpaid
//
// Each order lands in exactly one bucket via getBucket() below (priority order,
// closed > ops > vendor > attention > dispatch-team).
//
// Click a bucket → filters the table below to that bucket (via the `bucket:*`
// pseudo-value the page understands in IntakeBatchDetail's filter predicate).
// The "N overdue" chip filters further to orders whose scheduled date has
// already passed but haven't shipped.

import LifecycleBadge from '../common/LifecycleBadge';
import {
  AlertTriangle, Users2, HandMetal, Truck, CheckCircle2, Clock,
  PackageCheck, PackageX, Hash, HelpCircle,
} from 'lucide-react';

// Bucket definition — label, icon, tone (tailwind classes), and the
// predicate that decides whether an order belongs here.
// Predicates are evaluated in BUCKET_ORDER; first match wins.
const BUCKETS = [
  {
    key:   'closed',
    label: 'Closed',
    icon:  CheckCircle2,
    tone:  'bg-gray-50 border-gray-200 text-gray-700',
    match: (o) => ['completed', 'cancelled', 'delivered_unpaid'].includes(o.lifecycle_stage),
  },
  {
    key:   'ops',
    label: 'With Ops',
    icon:  HandMetal,
    tone:  'bg-blue-50 border-blue-200 text-blue-800',
    match: (o) => o.current_owner_team === 'ops',
  },
  {
    key:   'vendor_transit',
    label: 'With vendor / In transit',
    icon:  Truck,
    tone:  'bg-indigo-50 border-indigo-200 text-indigo-800',
    match: (o) => ['ready_for_dispatch', 'with_vendor', 'in_transit'].includes(o.lifecycle_stage),
  },
  {
    key:   'needs_attention',
    label: 'Needs attention',
    icon:  AlertTriangle,
    tone:  'bg-red-50 border-red-200 text-red-800',
    match: (o) => ['blocked_troublesome_customer', 'needs_attention',
                   'data_incomplete', 'reship_unlinked'].includes(o.lifecycle_stage),
  },
  {
    key:   'dispatch_team',
    label: 'With Dispatch team',
    icon:  Users2,
    tone:  'bg-amber-50 border-amber-200 text-amber-800',
    // Catch-all for anything not yet matched — intake / in_review owned by
    // dispatch team. Kept last so it can't steal orders that also fit an
    // earlier bucket.
    match: () => true,
  },
];

export function getBucket(order) {
  for (const b of BUCKETS) {
    if (b.match(order)) return b.key;
  }
  return 'dispatch_team';
}

// Order is overdue if it has a scheduled date that's in the past and hasn't
// reached a closed state yet. next_scheduled_date is pre-computed on the
// server (batch.service.js::listOrders).
function isOverdue(order) {
  if (!order.next_scheduled_date) return false;
  if (['completed', 'cancelled', 'delivered_unpaid'].includes(order.lifecycle_stage)) return false;
  return order.next_scheduled_date < new Date().toISOString().slice(0, 10);
}

export { isOverdue };

// Tracking metric definitions — each maps to a tracking:<key> filter the
// IntakeBatchDetail table predicate honours. Shipment counts come from the
// LATERAL rollup in batch.service.js::listOrders.
const TERMINAL_STAGES = ['cancelled', 'completed', 'delivered_unpaid'];

const TRACKING_METRICS = [
  {
    key:   'tn_assigned',
    label: 'TN assigned',
    icon:  Hash,
    tone:  'bg-emerald-50 border-emerald-200 text-emerald-800',
    match: (o) => !TERMINAL_STAGES.includes(o.lifecycle_stage)
               && (o.shipment_count || 0) > 0
               && (o.shipments_with_tn || 0) === (o.shipment_count || 0),
  },
  {
    key:   'tn_partial',
    label: 'TN partial',
    icon:  Hash,
    tone:  'bg-amber-50 border-amber-200 text-amber-800',
    match: (o) => !TERMINAL_STAGES.includes(o.lifecycle_stage)
               && (o.shipments_with_tn || 0) > 0
               && (o.shipments_with_tn || 0) < (o.shipment_count || 0),
  },
  {
    key:   'tn_missing',
    label: 'TN missing',
    icon:  HelpCircle,
    tone:  'bg-red-50 border-red-200 text-red-800',
    match: (o) => !TERMINAL_STAGES.includes(o.lifecycle_stage)
               && (o.shipment_count || 0) > 0
               && (o.shipments_with_tn || 0) === 0,
  },
  {
    key:   'carrier_live',
    label: 'Carrier tracking live',
    icon:  Truck,
    tone:  'bg-sky-50 border-sky-200 text-sky-800',
    match: (o) => (o.carrier_tracked_count || 0) > 0,
  },
  {
    key:   'carrier_delivered',
    label: 'Delivered',
    icon:  PackageCheck,
    tone:  'bg-green-50 border-green-200 text-green-800',
    match: (o) => (o.shipment_count || 0) > 0
               && (o.delivered_count || 0) === (o.shipment_count || 0),
  },
  {
    key:   'carrier_returned',
    label: 'Returned',
    icon:  PackageX,
    tone:  'bg-rose-50 border-rose-200 text-rose-800',
    match: (o) => (o.returned_count || 0) > 0,
  },
];

export function matchesTrackingMetric(order, key) {
  const m = TRACKING_METRICS.find((x) => x.key === key);
  return m ? m.match(order) : false;
}

export default function BatchStatusBreakdown({ orders, stageFilter, setStageFilter }) {
  if (!orders || orders.length === 0) return null;

  // Build: per-bucket total + per-bucket overdue + per-bucket per-stage counts
  // in one pass.
  const stats = {};
  for (const b of BUCKETS) {
    stats[b.key] = { total: 0, overdue: 0, byStage: {} };
  }
  for (const o of orders) {
    const k = getBucket(o);
    stats[k].total += 1;
    if (isOverdue(o)) stats[k].overdue += 1;
    const stageKey = o.lifecycle_stage === 'in_review' && o.current_owner_team
      ? `in_review:${o.current_owner_team}`
      : o.lifecycle_stage;
    stats[k].byStage[stageKey] = (stats[k].byStage[stageKey] || 0) + 1;
  }

  // Tracking rollup — counts of orders matching each metric.
  const trackingCounts = {};
  for (const m of TRACKING_METRICS) trackingCounts[m.key] = 0;
  for (const o of orders) {
    for (const m of TRACKING_METRICS) {
      if (m.match(o)) trackingCounts[m.key] += 1;
    }
  }

  return (
    <div className="mb-4 bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700">Status breakdown</h2>
        <span className="text-xs text-gray-500">{orders.length} orders total</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {BUCKETS.map((b) => {
          const s = stats[b.key];
          if (s.total === 0) return null;
          const filterValue = `bucket:${b.key}`;
          const overdueFilterValue = `overdue:${b.key}`;
          const active = stageFilter === filterValue;
          const Icon = b.icon;
          return (
            <div key={b.key}
                 className={`border rounded-lg p-3 ${b.tone} ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}>
              <button onClick={() => setStageFilter(active ? 'all' : filterValue)}
                      className="w-full text-left">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-80">
                  <Icon className="w-3.5 h-3.5" />
                  {b.label}
                </div>
                <div className="text-2xl font-bold mt-1">{s.total}</div>
              </button>

              {/* Overdue chip — only for non-closed buckets, and only when >0 */}
              {b.key !== 'closed' && s.overdue > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setStageFilter(stageFilter === overdueFilterValue ? 'all' : overdueFilterValue);
                  }}
                  className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
                    bg-amber-200 text-amber-900 hover:bg-amber-300
                    ${stageFilter === overdueFilterValue ? 'ring-1 ring-amber-600' : ''}`}>
                  <Clock className="w-3 h-3" /> {s.overdue} overdue
                </button>
              )}

              {/* Per-stage chips — smaller, for quick read */}
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(s.byStage).map(([stageKey, n]) => {
                  const [stage, ownerTeam] = stageKey.split(':');
                  return (
                    <span key={stageKey} className="inline-flex items-center gap-1">
                      <LifecycleBadge stage={stage} ownerTeam={ownerTeam} />
                      <span className="text-xs font-semibold opacity-70">×{n}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tracking breakdown — orthogonal to lifecycle buckets. Counts every
          order in the batch against each tracking state so admin can see
          'X have no TN yet, Y are live on India Post, Z delivered'. */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Tracking</h3>
          <span className="text-xs text-gray-500">
            {orders.reduce((sum, o) => sum + (o.shipments_with_tn || 0), 0)}
            {' / '}
            {orders.reduce((sum, o) => sum + (o.shipment_count || 0), 0)} shipments have a tracking number
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {TRACKING_METRICS.map((m) => {
            const count = trackingCounts[m.key];
            if (count === 0) return null;
            const filterValue = `tracking:${m.key}`;
            const active = stageFilter === filterValue;
            const Icon = m.icon;
            return (
              <button key={m.key}
                      onClick={() => setStageFilter(active ? 'all' : filterValue)}
                      className={`border rounded-lg p-2 text-left ${m.tone}
                        ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
                  <Icon className="w-3.5 h-3.5" />
                  {m.label}
                </div>
                <div className="text-xl font-bold mt-0.5">{count}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
